import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { ToastPositionEnum } from 'src/app/shared/services/toast/toast-position.enum';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import {
  deriveControllerGroupLabel,
  PermissionEntry,
  PermissionGroupCatalogEntry,
} from 'src/app/shared/components/permission-selector/permission-selector.component';
import { FrontFirstTreeGroupDto } from 'src/app/shared/components/front-first-permission-tree/front-first-permission-tree.types';
import { PermissionGroupService } from '../permission-groups/crud-service';
import { UserGroupPermissionService } from './crud-service';

/** @deprecated Use uma interface local em vez de importar daqui. Mantido por compatibilidade. */
export interface PeriodicElement {
  name?: string;
  position?: number;
  weight?: number;
  symbol?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-user-group-permissions',
  templateUrl: './user-group-permissions.component.html',
  styleUrls: ['./user-group-permissions.component.sass'],
  providers: [UserGroupPermissionService, PermissionGroupService],
})
export class UserGroupPermissionsComponent implements OnInit {
  @Output() changePage: EventEmitter<any> = new EventEmitter<any>();
  @Input('hidePagination') hidePagination: boolean;
  @Input() hasFilter: boolean = true;
  @Input('hideFilter') hideFilter: boolean;

  /** Cache RAW da API (systemActionParamId/name/allow) — fonte pro payload de save. */
  allPermissions: any[] = [];
  /** Catálogo macro pro app-permission-selector (grupos existentes, permission_groups). */
  permissionCatalog: PermissionGroupCatalogEntry[] = [];
  /** Lista plana pro app-permission-selector (derivada de `allPermissions`). */
  permissionsForSelector: PermissionEntry[] = [];
  /** Seleção atual (contrato imutável do app-permission-selector). */
  selectedIds: string[] = [];
  permissionsLoading = false;
  /** Árvore "Por tela" (own-only do ator). Vazia/erro → o seletor segue só Simples/Avançado. */
  frontFirstGroups: FrontFirstTreeGroupDto[] = [];
  frontFirstLoading = false;

  element: any;
  title: any = 'Permissoes do Tipo de Usuario';
  params: any = {
    getRelation: 1,
    limit: 5000,
    page: 0,
    filter: '',
  };
  length: number = 0;
  applied: boolean = false;

  constructor(
    public dialog: MatDialog,
    private service: UserGroupPermissionService,
    private permissionGroupService: PermissionGroupService,
    public dialogRef: MatDialogRef<UserGroupPermissionsComponent>,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA)
    data: { type: TypeIndexEnum; select: boolean; element: any }
  ) {
    this.element = data?.element || null;
    this.title += ' - ' + (this.element?.name || '');
    this.params = { ...this.params, userGroup: this.element?.id || null };
  }

  ngOnInit(): void {
    this.dialogRef.beforeClosed().subscribe(() =>
      this.dialogRef.close({ element: { applied: this.applied } })
    );
  }

  ngAfterViewInit(): void {
    this.loadPermissionGroups();
    this.loadFrontFirstTree();
    this.loadList();
  }

  /** Carrega a árvore "Por tela"; qualquer falha só esconde o modo (Simples/Avançado seguem). */
  loadFrontFirstTree(): void {
    this.frontFirstLoading = true;
    this.permissionGroupService.getFrontFirstTree().subscribe({
      next: (res) => {
        this.frontFirstGroups = Array.isArray(res?.groups) ? res.groups : [];
        this.frontFirstLoading = false;
      },
      error: () => {
        this.frontFirstGroups = [];
        this.frontFirstLoading = false;
      },
    });
  }

  loadPermissionGroups(): void {
    this.permissionGroupService
      .listAll({ getRelation: 1, limit: 5000, page: 0, filter: '' })
      .subscribe(([rows]: any) => {
        const groups = Array.isArray(rows) ? rows : [];
        this.permissionCatalog = groups
          .map((group: any) => ({
            id: String(group?.id || ''),
            name: String(group?.name || ''),
            permissionIds: Array.isArray(group?.permissionIds)
              ? group.permissionIds.map((id: any) => String(id))
              : Array.isArray(group?.permissions)
                ? group.permissions
                    .map((permission: any) => String(permission?.id || ''))
                    .filter((id: string) => !!id)
                : [],
          }))
          .filter((group: PermissionGroupCatalogEntry) => !!group.id && !!group.name);
      });
  }

  public loadList(): void {
    if (!this.element?.id) {
      this.allPermissions = [];
      this.permissionsForSelector = [];
      this.selectedIds = [];
      this.length = 0;
      return;
    }

    this.permissionsLoading = true;
    this.service.listAll(this.params).subscribe((dataWithPagination: any) => {
      const data = dataWithPagination[0];
      this.length = dataWithPagination[1];
      this.allPermissions = data || [];
      this.rebuildSelectorInputs();
      this.permissionsLoading = false;
    });
  }

  /**
   * Deriva `permissionsForSelector` (flat) e `selectedIds` a partir do cache
   * RAW `allPermissions`. Nome técnico (`sa_name`/`systemAction.name`) vira
   * `groupHint` só quando disponível na resposta — sem exigir mudança de
   * backend (o app-permission-selector deriva grupo sintético via GERAL
   * quando ausente, ou usa `permissionCatalog` quando ele tiver conteúdo).
   */
  private rebuildSelectorInputs(): void {
    this.permissionsForSelector = this.allPermissions.map((item) => {
      const actionName = this.extractActionName(item);
      return {
        id: String(item.systemActionParamId),
        name: item.name,
        groupHint: actionName ? deriveControllerGroupLabel(actionName) : undefined,
      };
    });
    this.selectedIds = this.allPermissions
      .filter((item) => !!item.allow)
      .map((item) => String(item.systemActionParamId));
  }

  /** Nome técnico (Controller_metodo) se a resposta trouxer a relação `systemAction`. */
  private extractActionName(item: any): string | undefined {
    return (
      item?.sa_name ||
      item?.systemAction?.name ||
      item?.actionName ||
      undefined
    );
  }

  onSelectedIdsChange(ids: string[]): void {
    this.selectedIds = ids;
  }

  public confirm(): void {
    if (!this.element?.id) {
      this.toastService.showError('Selecione um tipo de usuario para alterar as permissoes.');
      return;
    }

    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: 'CONFIRMACAO' },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          const selected = new Set(this.selectedIds.map(String));
          this.service
            .changeActives(
              this.allPermissions.map((item) => {
                const id =
                  item.systemActionParamId ||
                  item.systemActionParam?.id ||
                  item.systemActionParam;
                return {
                  userGroup: this.element.id,
                  systemActionParam: id,
                  allow: selected.has(String(id)),
                };
              })
            )
            .subscribe({
              next: () => {
                this.toastService.showSuccess(MessagesEnum.SUCCESS_OPERATION, {
                  position: ToastPositionEnum.BOTTOM_END,
                  toast: true,
                  timer: 3000,
                  showConfirmButton: false,
                  backdrop: '',
                });
                this.applied = true;
              },
              error: (error: any) => {
                const raw = error?.error?.message || error?.message;
                const message = Array.isArray(raw)
                  ? raw.join('; ')
                  : raw || 'Nao foi possivel salvar as permissoes.';
                this.toastService.showError(message);
              },
            });
        }
      },
    });
  }
}
